import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Text } from "@/components/catalyst/Text";
import { orpc } from "@/orpc/client";

type CancelSubscriptionDialogProps = {
	isOpen: boolean;
	onClose: () => void;
};

/**
 * Dialog for canceling a subscription at the end of the billing period.
 */
export function CancelSubscriptionDialog({
	isOpen,
	onClose,
}: CancelSubscriptionDialogProps) {
	const queryClient = useQueryClient();

	const { mutate: cancelSubscription, isPending } = useMutation(
		orpc.billing.cancelSubscription.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.billing.getSubscription.key(),
				});
				toast.success(
					"Your subscription will be cancelled at the end of the billing period",
				);
				onClose();
			},
			onError: (error) => {
				toast.error(error.message || "Failed to cancel subscription");
			},
		}),
	);

	const handleConfirm = () => {
		cancelSubscription({});
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Cancel Subscription</DialogTitle>
			<DialogDescription>
				Are you sure you want to cancel your subscription?
			</DialogDescription>
			<DialogBody>
				<div className="flex items-start gap-3 p-4 rounded-lg bg-warning/10 border border-warning/20">
					<AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
					<Text className="text-warning">
						Your subscription will remain active until the end of your current
						billing period. After that, you'll have read-only access to your
						dashboard.
					</Text>
				</div>
			</DialogBody>
			<DialogActions>
				<Button plain onClick={onClose} disabled={isPending}>
					Keep Subscription
				</Button>
				<Button onClick={handleConfirm} disabled={isPending} color="red">
					{isPending ? "Processing..." : "Cancel Subscription"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
